import type {
  Diagnostic,
  ProposedShift,
  RotaProposal,
  RuleEvaluationDiagnostic,
  ShiftAssignments,
  Worker,
} from '@repo/contracts';

import { formatTime } from '@/domain/templates/preview-utils';

/**
 * Builds per-day, read-only display data for the proposal view: each day's
 * shifts joined to their assigned worker (one `ProposedShift` per head), plus
 * the count of unfilled ("short") shifts that day. A shift is filled iff an
 * assignment on the same day references its id.
 */
export function buildProposalDays(proposal: RotaProposal): ProposalDayData[] {
  return proposal.days.map((day) => {
    const byShiftId = indexAssignments(day.assignments);
    const cells = day.shifts.map((shift) => {
      const assignment = byShiftId.get(shift.id);
      return { shift, worker: assignment?.worker, filled: assignment !== undefined };
    });
    return { date: day.date, cells, shortCount: cells.filter((cell) => !cell.filled).length };
  });
}

/** Total number of unfilled shifts across all days. */
export function totalShortCount(days: ProposalDayData[]): number {
  return days.reduce((total, day) => total + day.shortCount, 0);
}

/**
 * Collapses a day's shift cells into "decks": one stack per shift type + time
 * range, carrying the total slot count, how many are unfilled, and the
 * underlying cells (so the popover can still list assignees and the unfilled
 * "why"). Mirrors the live preview's stacking so a manager can see the shape of
 * a generated day at a glance. Insertion order is preserved.
 */
export function buildProposalStacks(cells: ProposalShiftCell[]): ProposalStack[] {
  const stacks: ProposalStack[] = [];
  const byKey = new Map<string, ProposalStack>();
  for (const cell of cells) {
    const typeName = cell.shift.shiftType.name;
    const time = `${formatTime(cell.shift.start)}–${formatTime(cell.shift.end)}`;
    const key = `${typeName} ${time}`;
    let stack = byKey.get(key);
    if (stack === undefined) {
      stack = { key, typeName, time, count: 0, shortCount: 0, cells: [] };
      byKey.set(key, stack);
      stacks.push(stack);
    }
    stack.count += 1;
    if (!cell.filled) stack.shortCount += 1;
    stack.cells.push(cell);
  }
  return stacks;
}

/** The diagnostics that reference a given shift — the per-shift "why". */
export function diagnosticsForShift(
  diagnostics: Diagnostic[] | undefined,
  shiftId: string,
): Diagnostic[] {
  return (diagnostics ?? []).filter(
    (diagnostic) =>
      (diagnostic.type === 'RuleEvaluation' || diagnostic.type === 'Shift') &&
      diagnostic.refs.shiftId === shiftId,
  );
}

/**
 * The distinct scheduling rules referenced for a shift, read from its
 * correlated rule-evaluation diagnostics — each failed rule evaluation is its
 * own diagnostic (`type: 'RuleEvaluation'`), linked to the shift via
 * `refs.shiftId` rather than nested inside a parent — humanised
 * (`"rest-between-shifts"` → `"Rest between shifts"`). Deduplicated, so a
 * rule that blocked forty workers shows once. Powers the per-shift "why"
 * popover. Includes rules from every stage — a shift can be blocked by a
 * candidate-stage mismatch as much as an eligibility failure.
 */
export function blockingRulesForShift(
  diagnostics: Diagnostic[] | undefined,
  shiftId: string,
): string[] {
  const ruleIds = new Set<string>();
  for (const diagnostic of diagnostics ?? []) {
    if (!isRuleEvaluationDiagnostic(diagnostic)) continue;
    if (diagnostic.refs.shiftId !== shiftId) continue;
    ruleIds.add(diagnostic.code);
  }
  return [...ruleIds].map(humaniseRuleId);
}

/**
 * Workers with recurring scheduling conflicts worth a manager's attention:
 * those who hit a **working-time / availability / compliance** rule (the
 * `eligibility`-stage rules — rest, days-off, max-consecutive, WTD, overlap…)
 * on two or more shifts. Deliberately ignores `candidate`-stage role/skill
 * mismatches, which are routine (a worker simply isn't suited to other roles)
 * and would otherwise bury the real conflicts. Worker names resolve from the
 * proposal's assignments, since diagnostics carry only the worker id.
 */
export function workerConflicts(proposal: RotaProposal): WorkerConflict[] {
  const names = workerNamesById(proposal);
  const byWorker = new Map<string, { shifts: Set<string>; rules: Set<string> }>();

  for (const diagnostic of proposal.diagnostics ?? []) {
    if (!isRuleEvaluationDiagnostic(diagnostic)) continue;
    if (diagnostic.stage !== 'eligibility') continue;
    if (!CONFLICT_OUTCOMES.has(diagnostic.outcome)) continue;

    const refs = diagnostic.refs;
    const workerId = refs.workerId;
    if (workerId === undefined) continue;

    const entry = byWorker.get(workerId) ?? { shifts: new Set<string>(), rules: new Set<string>() };
    entry.shifts.add(refs.shiftId);
    entry.rules.add(diagnostic.code);
    byWorker.set(workerId, entry);
  }

  return [...byWorker.entries()]
    .filter(([, entry]) => entry.shifts.size >= RECURRING_THRESHOLD)
    .map(([workerId, entry]) => ({
      workerId,
      workerName: names.get(workerId),
      shiftCount: entry.shifts.size,
      rules: [...entry.rules].map(humaniseRuleId),
    }));
}

/** Outcomes that count as a conflict. */
const CONFLICT_OUTCOMES = new Set<Diagnostic['outcome']>(['warning', 'error']);

/** How many conflicting shifts a worker needs before the conflict is "recurring". */
const RECURRING_THRESHOLD = 2;

/** Indexes a day's assignments by shift id (a shift id is unique within a day). */
function indexAssignments(assignments: ShiftAssignments[]): Map<string, ShiftAssignments> {
  const map = new Map<string, ShiftAssignments>();
  for (const assignment of assignments) map.set(assignment.shiftId, assignment);
  return map;
}

/** Builds a worker-id → name map from every assignment in the proposal. */
function workerNamesById(proposal: RotaProposal): Map<string, string> {
  const map = new Map<string, string>();
  for (const day of proposal.days) {
    for (const assignment of day.assignments) map.set(assignment.worker.id, assignment.worker.name);
  }
  return map;
}

/** `"rest-between-shifts"` → `"Rest between shifts"`. */
function humaniseRuleId(ruleId: string): string {
  const spaced = ruleId.replace(/[-_]/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Narrows a diagnostic to a rule-evaluation diagnostic, exposing its `stage` and `refs`. */
function isRuleEvaluationDiagnostic(
  diagnostic: Diagnostic,
): diagnostic is RuleEvaluationDiagnostic {
  return diagnostic.type === 'RuleEvaluation';
}

/** One day's shifts with their fill state, plus the day's short count. */
export interface ProposalDayData {
  date: string;
  cells: ProposalShiftCell[];
  shortCount: number;
}

/** A single shift in the proposal grid: the shift, its worker (if filled), and the fill state. */
export interface ProposalShiftCell {
  shift: ProposedShift;
  worker?: Worker;
  filled: boolean;
}

/** A deck of same-type, same-time shifts on a day, with its fill breakdown. */
export interface ProposalStack {
  /** Stable grouping key (`type + time`). */
  key: string;
  typeName: string;
  /** Formatted time range, e.g. `09:00–17:00`. */
  time: string;
  /** Total slots in the deck. */
  count: number;
  /** How many of those slots are unfilled. */
  shortCount: number;
  /** The underlying cells, for listing assignees and the unfilled "why". */
  cells: ProposalShiftCell[];
}

/** A worker with recurring working-time / availability conflicts. */
export interface WorkerConflict {
  workerId: string;
  workerName?: string;
  /** Number of shifts where this worker hit a working-time / availability rule. */
  shiftCount: number;
  /** Distinct working-time / availability rules they hit, humanised. */
  rules: string[];
}
